import BasicDialog from "../../base/BasicDialog";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import useOfferService from "../../../services/offerService";
import { useAsync, useAsyncFn } from "../../../hooks/useAsync";
import { useSnackbarAlert } from "../../../context/snackbarAlert";
import ItemForm from "../../form/ItemForm/ItemForm";
import useDialog from "../../../hooks/useDialog";
import SubcontractorDetailsDialog from "../SubcontractorDetailsDialog/SubcontractorDetailsDialog";
import ItemExtendForm from "../../form/ItemExtendForm/ItemExtendForm";
import useItemService from "../../../services/itemService";
import BoxWithTypography from "../../box/BoxWithTypography/BoxWithTypography";
import ItemRecord from "../../other/ItemRecord/ItemRecord";
import FileUploadList from "../../other/FileUploadList";
import BaseBox from "../../base/BaseBox/baseBox";
import LoadingButton from "@mui/lab/LoadingButton";
import { Grid } from "@mui/material";
import {
  INTERNAL_TYPE,
  MATERIAL_MAIN_BRANCH_NAME,
  SERVICE_MAIN_BRANCH_NAME,
  DEVICE_MAIN_BRANCH_NAME,
  COUNTER_BRANCH_NAME,
} from "../../../helpers/constants";
import useLocationService from "../../../services/locationService";
import { useForm } from "../../../hooks/useForm";
import LoaderWrapper from "../../wrapper/LoaderWrapper";
import useFileService from "../../../services/fileService";
import useFileOnMemoryData from "../../../hooks/useFileOnMemoryData";
import useItemFormValidation from "../../../hooks/useItemFormValidation";
import PartParameterDialog from "../PartParameterDialog";
import PartParamsTable from "../../table/PartParamsTable";
import { getErrorMsg, isEmptyObject, isEmptyValue } from "../../../helpers/methods";
import SelectItemDialog from "../SelectItemDialog";
import ItemEquipmentForm from "../../form/ItemEquipmentForm";
import useEquipmentService from "../../../services/equipmentService";
import useDocsData from "../../../hooks/useDocsData";
import useCertsData from "../../../hooks/useCertsData";
import useEnclosuresData from "../../../hooks/useEnclosuresData";

const CreateItemDialog = (props) => {
  const { t } = useTranslation();
  const [localId, setLocalId] = useState(props.itemId)
  const [parametersLocal, setParametersLocal] = useState([]);

  const {
    createNewService,
    createNewPart,
    editPartData,
    editServiceData,
    getPartFilteringData,
    getServiceFilteringData,
    getServiceDetailsData,
    getPartDetailsData
  } = useOfferService();

  const { createNewEquipmentItem, getEquipmentForCreateMeterFilteringData, getEquipmentItemDetials, updateEquipment } =
    useEquipmentService();

  const {
    getPartParametersList,
    getEmptyItemElement,
    getItemRecordById,
    prepareItemDataFromBackendForItemRecordComponent,
  } = useItemService();

  const createNewServiceFn = useAsyncFn(createNewService);
  const createNewPartFn = useAsyncFn(createNewPart);
  const createEquipmentFn = useAsyncFn(createNewEquipmentItem);

  const updatePartDataFn = useAsyncFn(editPartData);
  const updateServiceDataFn = useAsyncFn(editServiceData)
  const updateEquipmentFn = useAsyncFn(updateEquipment);


  const filteringData = useAsync(() => {
    if (props.itemType === "service") {
      return getServiceFilteringData();
    }
    if (props.itemType === "equipment") {
      return getEquipmentForCreateMeterFilteringData();
    }
    return getPartFilteringData();
  }, [props.itemType]);


  const itemDetailsData = useAsync(() => {
    if (!isEmptyValue(localId)) {
      if (props.itemType === "part") {
        return getPartDetailsData(localId);
      }
      if (props.itemType === "service") {
        return getServiceDetailsData(localId);
      }
      if (props.itemType === "equipment") {
        return getEquipmentItemDetials(localId);
      }
    }
    return Promise.resolve({});
  }, [localId, props.itemType]);


  useEffect(() => {
    if (itemDetailsData.loading) {
      return;
    }
    if (itemDetailsData.value && !isEmptyObject(itemDetailsData.value)) {
      if (props.isCopyObject) {
        setFormValue({ ...itemDetailsData.value, id: null });
        setLocalId(undefined)
      }
      setFormValue(itemDetailsData.value);
      setItems(
        prepareItemDataFromBackendForItemRecordComponent(
          itemDetailsData.value.item
        )
      );
    }
  }, [itemDetailsData.loading]);


  const partParametersData = useAsync(() => {
    // if (localId) {
    //   return getPartParametersList(localId);
    // }
    return Promise.resolve([]);
  }, [localId]);


  useEffect(() => {
    if (partParametersData.loading) {
      return;
    }
    if (partParametersData.value) {
      setParametersLocal(partParametersData.value);
    }
  }, [partParametersData.loading]);


  const snackbarAlert = useSnackbarAlert();


  const { getChosenHighestLevelObject } = useLocationService();

  const [items, setItems] = useState(getEmptyItemElement());
  const [parameters, setParameters] = useState([]);

  const getItemRecordByIdFn = useAsyncFn(getItemRecordById);

  const showLoading = filteringData.loading;

  const { downloadFileByBlobUrl } = useFileService();

  const {
    formValue,
    setFormValue,
    onChange,
    onChangeDate,
    onChangeAutocompleteFieldWithObjectOptions,
  } = useForm(props.initialFormValue);

  const {
    unfillRequiredFields,
    locationRecordsRef,
    checkIfRequiredFieldsAreFill,
  } = useItemFormValidation(props.itemType, formValue);

  const [
    memoryEnclosures,
    onAddMemoryEnclosure,
    onUpdateMemoryEnclosure,
    onDeleteMemoryEnclosure,
    prepareMemoryEnclosuresToSend,
  ] = useFileOnMemoryData();

  const [
    certificates,
    onAddCert,
    onUpdateCert,
    onDeleteCert,
    prepareCertsToSend,
  ] = useFileOnMemoryData("certs");

  const {
    enclosures,
    saveEnclosures,
    onPreviewEnclosure,
    onDownloadEnclosure,
    deleteEnclosureForPart,
    updatePartEnclosure,
  } = useEnclosuresData(localId, undefined, "part");


  const {
    certs,
    saveCerts,
    updateCerts,
    hideCerts,
    refetchCerts,
    onDownloadCert,
    onPreviewCert,
    isCertsLoading
  } = useCertsData(localId);



  const onDownloadMemoryEnclosure = (enclosureId, enclosureName, index) => {
    const enclosure = memoryEnclosures[index];
    downloadFileByBlobUrl(enclosure.blob_url, enclosureName);
  };

  const onDownloadMemoryCert = (certId, certName, index) => {
    const cert = certificates[index];
    downloadFileByBlobUrl(cert.blob_url, certName);
  };

  const [
    openAddPartParameterDialog,
    onOpenAddPartParameterDialog,
    onCloseAddPartParameterDialog,
  ] = useDialog();

  const [
    openSelectItemDialog,
    onOpenSelectItemDialog,
    onCloseSelectItemDialog,
  ] = useDialog();

  const onCreateNewItem = (newItemData) => {
    if (props.itemType === "service") {
      createNewServiceFn
        .execute(newItemData)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.service_created")
          );
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          if (props.onSubmit) {
            props.onSubmit(res);
          }

          props.onClose(res.id);
        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(error.data),
            t("snackbar_alert.occurred_error_during_service_data_creating")
          );
        });
    } else if (props.itemType === "equipment") {
      createEquipmentFn
        .execute(newItemData)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.equipment_created")
          );
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          if (props.onSubmit) {
            props.onSubmit(res);
          }
          props.onClose(true);
        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(error.data),
            t("snackbar_alert.occurred_error_during_saving_changes")
          );
        });
    } else {
      createNewPartFn
        .execute(newItemData)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.part_created")
          );
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          if (props.onSubmit) {
            props.onSubmit(res);
          }
          props.onClose(res.id);
        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(error.data),
            t("snackbar_alert.occurred_error_during_creating_new_part")
          );
        });
    }
  };


  const onUpdateItem = useCallback((itemData) => {
    if (props.itemType === "service") {
      updateServiceDataFn
        .execute(localId, itemData)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.service_data_updated")
          );
          if (props.onRefetchData) {
            props.onRefetchData();
          }
          props.onClose();
        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(error.data),
            t("snackbar_alert.occurred_error_during_service_data_updating")
          );
        })
    } else if (props.itemType === "equipment") {
      updateEquipmentFn
        .execute(localId, itemData)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.equipment_updated")
          );
          itemDetailsData.refetch()
        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(error.data),
            t("snackbar_alert.occurred_error_during_equipment_data_updating")
          );
        });
    } else {
      updatePartDataFn
        .execute(localId, itemData)
        .then((res) => {
          snackbarAlert.openSuccessSnackbarAlert(
            t("snackbar_alert.part_data_updated")
          );
          if (props.onRefetchData) {
            props.onRefetchData();
          }

          props.onClose();

        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            getErrorMsg(error.data),
            t("snackbar_alert.occurred_error_during_part_data_updating")
          );
        });
    }
  }, [props.itemType])

  const onSubmit = (itemData) => {
    if (isEmptyValue(localId)) {
      onCreateNewItem(itemData)
    }
    else {
      onUpdateItem(itemData)
    }
  };

  const onChangeLevelItem = (item, index = 0) => {
    var items_temp = [...items];
    items_temp[index] = item;
    setItems(item);
  };

  const onAddNewParameter = useCallback(
    (parameter) => {
      if (isEmptyValue(localId)) {
        onAddNewParameterLocal(parameter)
      } 
    },
    [formValue, parameters, localId]
  );



  const onAddNewParameterLocal = useCallback(
    (parameter) => {
      let tempParameters = [...parameters];
      tempParameters.push(parameter);
      setParameters(tempParameters);
      onCloseAddPartParameterDialog();
    },
    [formValue, parameters]
  );

  const onPrepareData = () => {
    formValue["item"] = getChosenHighestLevelObject(items);
    if (props.itemType === "part" || props.itemType === "equipment") {
      formValue["files"] = prepareMemoryEnclosuresToSend(memoryEnclosures).concat(
        prepareCertsToSend(certificates)
      );
      formValue["parameters"] = parameters;
    }

    if (checkIfRequiredFieldsAreFill()) {
      onSubmit(formValue);
    }
  };

  const getItemRecord = useCallback(
    (selectedItemId) => {
      getItemRecordByIdFn
        .execute(selectedItemId)
        .then((result) => {
          setItems(
            prepareItemDataFromBackendForItemRecordComponent(result.item)
          );
          onCloseSelectItemDialog();
        })
        .catch((error) => {
          snackbarAlert.openErrorSnackbarAlert(
            t("snackbar_alert.occurred_error_during_get_item_data")
          );
        });
    },
    [items]
  );

  const itemProps = {
    unfillRequiredFields: unfillRequiredFields,
    itemType: props.itemType,
    readOnly: false,
    partChoiceSelectData: filteringData,
    filteringData: filteringData,
    formValue: formValue,
    setFormValue: setFormValue,
    onChange: onChange,
    onChangeDate: onChangeDate,
    onChangeAutocompleteFieldWithObjectOptions:
      onChangeAutocompleteFieldWithObjectOptions,
  };

  return (
    <BasicDialog
      open={props.open}
      onClose={(e) => props.onClose()}
      titleAlign="center"
      maxWidth={"xl"}
      title={localId ? t(`dialog.item_dialog.edit_${props.itemType}`) : t(`dialog.item_dialog.add_${props.itemType}`)}
    >
      <LoaderWrapper showLoader={showLoading}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {props.itemType === "part" ? (
              <ItemExtendForm {...itemProps} />
            ) : props.itemType === "equipment" ? (
              <ItemEquipmentForm {...itemProps} />
            ) : (
              <ItemForm {...itemProps} />
            )}
          </Grid>
          <Grid item xs={12}>
            <BoxWithTypography
              style={{ padding: "10px" }}
              label={t(`form.item_form.purchase_tree`)}
              component={"div"}
            >
              <ItemRecord
                ref={locationRecordsRef}
                itemLevels={items}
                onChangeItem={onChangeLevelItem}
                onClickSearchItem={onOpenSelectItemDialog}
                defaultFirstLevelItemName={
                  props.itemType === "part"
                    ? MATERIAL_MAIN_BRANCH_NAME
                    : props.itemType === "equipment"
                      ? DEVICE_MAIN_BRANCH_NAME
                      : SERVICE_MAIN_BRANCH_NAME
                }
                defaultSecondLevelItemName={
                  props.itemType === "equipment" ? COUNTER_BRANCH_NAME : null
                }
              />
            </BoxWithTypography>
          </Grid>
          {(props.itemType === "part" || props.itemType === "equipment") && (
            <>
              <Grid item xs={3}>
                {
                  <PartParamsTable
                    partsParamsData={localId ? parametersLocal || [] : parameters}
                    readOnly={localId ? props.readOnly: false}
                    onHandleAddNewParameter={props.itemType === "equipment" ?onOpenAddPartParameterDialog: undefined}
                    showParametersContextMenu={false}
                    itemType={props.itemType}
                  />
                 }
              </Grid>
              <Grid item xs={4.5}>
                <BaseBox>
                  {localId ?
                    <FileUploadList
                      addEnclosureButtonProps={{ size: "mini" }}
                      defaultEnclosureType={INTERNAL_TYPE}
                      enclosures={enclosures}
                      onPreviewEnclosure={onPreviewEnclosure}
                      onDownloadEnclosure={onDownloadEnclosure}
                      onUpdateEnclosure={updatePartEnclosure}
                      onDeleteEnclosure={deleteEnclosureForPart}
                      onAddEnclosure={saveEnclosures}
                      canRemoveEnclosures={true}
                      readOnly={false}
                      showExtendedAddEnclosureDialog={true}
                      showSubcontractorSelect={false}
                      showEnclosureType={false}
                      showEnclosurePartKind={true}
                      allowCopyEnclosure={false}
                    />
                    :
                    <FileUploadList
                      addEnclosureButtonProps={{ size: "mini" }}
                      defaultEnclosureType={INTERNAL_TYPE}
                      enclosures={memoryEnclosures}
                      onDownloadEnclosure={onDownloadMemoryEnclosure}
                      onAddEnclosure={onAddMemoryEnclosure}
                      onDeleteEnclosure={onDeleteMemoryEnclosure}
                      onUpdateEnclosure={onUpdateMemoryEnclosure}
                      showExtendedAddEnclosureDialog={true}
                      showSubcontractorSelect={false}
                      showEnclosureType={false}
                      showEnclosurePartKind={props.itemType === "part"}
                      allowCopyEnclosure={false}
                    />}
                </BaseBox>
              </Grid>

              <Grid item xs={4.5}>
                <BaseBox>
                  {localId ?
                    <FileUploadList
                      addFileButtonProps={{ size: "mini" }}
                      enclosures={certs}
                      fileType={"certs"}
                      onAddEnclosure={saveCerts}
                      onPreviewEnclosure={onPreviewCert}
                      onDownloadEnclosure={onDownloadCert}
                      onDeleteEnclosure={hideCerts}
                      onUpdateEnclosure={updateCerts}
                      canRemoveEnclosures={!props.readOnly}
                      onRefetch={refetchCerts}
                      readOnly={props.readOnly || isCertsLoading}

                    />
                    :
                    <FileUploadList
                      addEnclosureButtonProps={{ size: "mini" }}
                      defaultEnclosureType={INTERNAL_TYPE}
                      enclosures={certificates}
                      onDownloadEnclosure={onDownloadMemoryCert}
                      onAddEnclosure={onAddCert}
                      onDeleteEnclosure={onDeleteCert}
                      onUpdateEnclosure={onUpdateCert}
                      fileType={"certs"}
                      showDetailsButton={false}
                    />}
                </BaseBox>
              </Grid>
            </>
          )}
          <Grid item xs={15}>
            <LoadingButton
              variant="contained"
              color="primary"
              fullWidth
              loading={props.isLoading}
              onClick={onPrepareData}
            >
              {localId ? t("save_changes") : t("create")}
            </LoadingButton>
          </Grid>
        </Grid>
        {openAddPartParameterDialog && (
          <PartParameterDialog
            open={openAddPartParameterDialog}
            onClose={onCloseAddPartParameterDialog}
            onAddParameter={onAddNewParameter}
            isLoading={false}
            partId={localId}
            itemType={props.itemType}
            restrictParamListForItem={props.restrictParamListForItem}
            itemId={getChosenHighestLevelObject(items)}
          />
        )}
        {openSelectItemDialog && (
          <SelectItemDialog
            open={openSelectItemDialog}
            onClose={onCloseSelectItemDialog}
            onSubmit={getItemRecord}
            itemType={props.itemType}
            treeIndex={props.treeIndex}
          />
        )}
      </LoaderWrapper>
    </BasicDialog>
  );
};

CreateItemDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  itemType: PropTypes.string,
  onRefetchData: PropTypes.bool,
  readOnly: PropTypes.bool,
  isButtonAddItemAvailable: PropTypes.bool,
  formType: PropTypes.string,
  showSearchItemButton: PropTypes.bool,
  restrictParamListForItem: PropTypes.bool,
  treeIndex: PropTypes.number,
  initialFormValue: PropTypes.object,
  isCopyObject: PropTypes.bool
};

CreateItemDialog.defaultProps = {
  itemType: "service",
  readOnly: true,
  isButtonAddItemAvailable: true,
  showSearchItemButton: true,
  restrictParamListForItem: true,
  treeIndex: 1,
  initialFormValue: {},
  isCopyObject: false
};

export default CreateItemDialog;
